import { useCallback, useState } from "react";

import { naturalCompare } from "@/utils/naturalCompare";

import type { DriveNode, DriveShare } from "./types";

export type SortKey = "name" | "uploader" | "created" | "modified" | "size";
export type SortDir = "asc" | "desc";

export interface SortState {
  key: SortKey;
  dir: SortDir;
}

/**
 * A row reduced to the fields it may be sorted by. The node listing („Moje pliki",
 * folder contents) and the two share listings carry the same facts under different
 * field names, so each maps its own rows onto this shape and shares one comparator.
 */
export interface SortableRow {
  isFolder: boolean;
  name: string;
  uploader: string;
  created: string;
  modified: string;
  size: number;
}

export const DEFAULT_SORT: SortState = { key: "name", dir: "asc" };

/** Columns every drive listing offers. */
export const DRIVE_SORT_KEYS: readonly SortKey[] = [
  "name",
  "uploader",
  "created",
  "modified",
  "size",
];

export const nodeSortRow = (node: DriveNode): SortableRow => ({
  isFolder: node.node_type === "folder",
  name: node.name,
  uploader: node.uploaded_by_name,
  created: node.created_at,
  modified: node.updated_at,
  size: node.size_bytes,
});

/** A share row describes the SHARED ITEM — never the share itself, whose own
 * `created_at` is the day it was shared, not the day the file was added. */
export const shareSortRow = (share: DriveShare): SortableRow => ({
  isFolder: share.node_type === "folder",
  name: share.node_name,
  uploader: share.node_uploaded_by_name,
  created: share.node_created_at,
  modified: share.node_updated_at,
  size: share.node_size_bytes,
});

/** First click on a header: dates and size start from the largest (newest on top —
 * the reason the columns were asked for), text columns from A. */
const FIRST_DIR: Record<SortKey, SortDir> = {
  name: "asc",
  uploader: "asc",
  created: "desc",
  modified: "desc",
  size: "desc",
};

export function nextSort(current: SortState, key: SortKey): SortState {
  if (current.key !== key) return { key, dir: FIRST_DIR[key] };
  return { key, dir: current.dir === "asc" ? "desc" : "asc" };
}

function time(iso: string): number {
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function compare(a: SortableRow, b: SortableRow, key: SortKey): number {
  switch (key) {
    case "name":
      return naturalCompare(a.name, b.name);
    case "uploader":
      return naturalCompare(a.uploader, b.uploader);
    case "created":
      return time(a.created) - time(b.created);
    case "modified":
      return time(a.modified) - time(b.modified);
    case "size":
      return a.size - b.size;
  }
}

/**
 * Sort a COPY of `rows`. Folders always come before files (as the backend orders
 * them) and are sorted among themselves by the same key; ties fall back to the name
 * so the order never wobbles between renders.
 */
export function sortBy<T>(
  rows: T[],
  toRow: (row: T) => SortableRow,
  sort: SortState,
): T[] {
  const sign = sort.dir === "asc" ? 1 : -1;
  return rows
    .map((item) => ({ item, row: toRow(item) }))
    .sort((a, b) => {
      if (a.row.isFolder !== b.row.isFolder) return a.row.isFolder ? -1 : 1;
      const primary = compare(a.row, b.row, sort.key) * sign;
      return primary !== 0 ? primary : naturalCompare(a.row.name, b.row.name);
    })
    .map(({ item }) => item);
}

const STORAGE_KEY = "elg5:two-drive:sort";

function storedSort(allowed: readonly SortKey[]): SortState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as Partial<SortState>;
      const key = saved.key;
      const dir = saved.dir;
      if (key && allowed.includes(key) && (dir === "asc" || dir === "desc")) {
        return { key, dir };
      }
    }
  } catch {
    // Private window / site data blocked — the default order is fine.
  }
  return DEFAULT_SORT;
}

/**
 * Sort order for one listing, remembered per browser so „newest first" survives
 * navigating into a folder and switching tabs. `allowed` lists the columns THIS
 * listing shows — a remembered column it lacks falls back to the default.
 */
export function useSortState(allowed: readonly SortKey[]) {
  const [sort, setSort] = useState<SortState>(() => storedSort(allowed));
  const toggle = useCallback((key: SortKey) => {
    setSort((current) => {
      const next = nextSort(current, key);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // As above — the order still applies for this view.
      }
      return next;
    });
  }, []);
  return [sort, toggle] as const;
}
